import { useGlobalContext } from '@/context/Global/GlobalContext';
import useAdminSignIn from '@/hooks/signIn/adminSignIn';
import { useSellerSignIn } from '@/hooks/signIn/sellerSignIn';
import { DashboardUserType } from '@/utils/enums';
import { WritableDraft } from 'immer';
import posthog from 'posthog-js';
import { ChangeEvent, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { loginInitialState, LoginUi } from './LoginUi';

export function useLoginViewController(loginType: DashboardUserType) {
  const { session } = useGlobalContext();
  const [uiState, setUiState] = useImmer<LoginUi>(loginInitialState);

  const { login: sellerSignIn, isLoginLoading: isSellerLoginLoading } =
    useSellerSignIn();
  const { login: adminSignIn, isLoginLoading: isAdminLoginLoading } =
    useAdminSignIn();

  const navigate = useNavigate();

  useEffect(() => {
    setUiState((prev) => {
      prev.isLoading = isSellerLoginLoading || isAdminLoginLoading;
    });
  }, [isSellerLoginLoading, isAdminLoginLoading]);

  const onUserChanged = (newUser: string) =>
    setUiState((prev) => {
      prev.user = newUser;
      calculateSubmitEnabled(prev);
    });

  const onPasswordChanged = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setUiState((prev) => {
      prev.password = e.target.value;
      prev.passwordError = '';
      calculateSubmitEnabled(prev);
      return prev;
    });
  };

  const togglePasswordVisibility = () =>
    setUiState((prev) => {
      prev.passwordVisible = !prev.passwordVisible;
    });

  const calculateSubmitEnabled = (input: WritableDraft<LoginUi>) => {
    input.signInButtonDisabled = !input.password.trim() || !input.user.trim();
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const credentials = {
      email: uiState.user,
      password: uiState.password,
    };

    const handleError = (e: unknown) => {
      setUiState((prev) => {
        prev.password = '';
        prev.passwordError =
          'Hubo un error de autenticación. Intentar otra vez.';
        prev.userError = ' ';
        console.error(e);
      });
    };

    if (loginType === DashboardUserType.SELLER) {
      sellerSignIn(credentials, {
        onSuccess: (user) =>
          posthog.identify(user.businessId, { userType: 'seller' }),
        onError: handleError,
      });
      return;
    }

    if (loginType === DashboardUserType.ADMIN) {
      adminSignIn(credentials, {
        onSuccess: (user) =>
          posthog.identify(user.userId, { userType: 'admin' }),
        onError: handleError,
      });
    }
  };

  useEffect(() => {
    if (!session) return;
    switch (session.type) {
      case DashboardUserType.ADMIN:
        navigate('/admin/seller', { replace: true });
        return;
      case DashboardUserType.SELLER:
        navigate(`/seller/orders`, { replace: true });
        return;
    }
  }, [session]);

  return {
    uiState,
    actions: {
      onUserChanged,
      onPasswordChanged,
      togglePasswordVisibility,
      onSubmit,
    },
  };
}
